/** Map marketplace API caregiver rows for browse UI + client-side filters. */

function extractKeyFromUrl(fileUrl) {
  const s = String(fileUrl || "").trim();
  if (!s) return "";
  try {
    const parsed = new URL(s, "http://local");
    return String(parsed.searchParams.get("key") || "").trim();
  } catch {
    return "";
  }
}

function parseList(value) {
  if (Array.isArray(value)) return value.map((s) => String(s).trim()).filter(Boolean);
  return String(value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function mapCaregiverDesignation(row) {
  const role = String(
    row.serviceRole || row.careType || row.profileExtended?.marketplacePrefs?.serviceRole || ""
  ).toLowerCase();
  if (role === "nurses") return "Staff Nurse";
  if (role === "physio") return "Physiotherapist";
  if (role === "dementia") return "Dementia Care Specialist";
  if (role === "post_operative") return "Post Operative Care";
  const cat = String(row.category || "").trim();
  if (/nurse/i.test(cat)) return "Staff Nurse";
  if (/physio/i.test(cat)) return "Physiotherapist";
  if (/dementia/i.test(cat)) return "Dementia Care Specialist";
  if (/post.?oper|surgery/i.test(cat)) return "Post Operative Care";
  return "Care Attendant";
}

export function isCaregiverVerified(c) {
  const kyc = String(c.kycStatus || c.aadhaar_kyc || "").toLowerCase();
  const police = String(c.policeVerification || c.police_verification || "").toLowerCase();
  const approved = c.adminApproved ?? c.admin_approved;
  return (
    ["verified", "approved", "complete"].includes(kyc) &&
    ["verified", "approved", "complete"].includes(police) &&
    approved !== false
  );
}

/** True when Ops/admin has approved the caregiver profile. */
export function isCaregiverAdminApproved(c) {
  const approved = c.adminApproved ?? c.admin_approved;
  if (approved === true || approved === 1 || approved === "1") return true;
  if (approved === false || approved === 0 || approved === "0") return false;
  if (c.verified === true) return true;
  return false;
}

/** True when a permanent NCID/NPID code has been issued. */
export function hasCaregiverNcidProfile(c) {
  if (c?.npid?.issued === true || c?.ncid?.issued === true) return true;
  const code =
    (typeof c?.npid === "string" ? c.npid : null) ||
    (typeof c?.ncid === "string" ? c.ncid : null) ||
    c?.npid?.code ||
    c?.ncid?.code ||
    c?.npidCode ||
    c?.ncidCode ||
    "";
  return Boolean(String(code).trim());
}

/** Footer role line: Verified · NCID Profile · Care Attendant (parts conditional). */
export function formatCaregiverRoleStatusLine(c) {
  const parts = [];
  if (isCaregiverAdminApproved(c)) parts.push("Verified");
  if (hasCaregiverNcidProfile(c)) parts.push("NCID Profile");
  parts.push(c?.designation || mapCaregiverDesignation(c) || "Care Attendant");
  return parts.join(" · ");
}

/** Parse finite lat/lng; treat null/empty as missing (do not coerce null → 0). */
function parseCoord(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function mapMarketplaceCaregiver(row, index = 0) {
  if (!row || typeof row !== "object") return null;
  const skills = parseList(row.skills);
  const languages = parseList(row.languages);
  const qualifications = parseList(row.qualifications);
  const latitude = parseCoord(row.latitude ?? row.mapLatitude ?? row.map_latitude);
  const longitude = parseCoord(row.longitude ?? row.mapLongitude ?? row.map_longitude);

  return {
    id: row.id || row.caregiverId || index,
    userId: row.userId ?? row.user_id ?? null,
    name: row.name || "Caregiver",
    rating: row.rating != null ? Number(row.rating) : null,
    reviewCount: Number(row.reviewCount) || 0,
    years: row.experience_years != null ? Number(row.experience_years) : row.years != null ? Number(row.years) : null,
    distanceKm: row.distanceKm != null ? Number(row.distanceKm) : row.distance_km != null ? Number(row.distance_km) : null,
    skills,
    qualifications,
    kycStatus: row.kycStatus || row.aadhaar_kyc || null,
    policeVerification: row.policeVerification || row.police_verification || null,
    adminApproved: row.adminApproved ?? row.admin_approved,
    verified: row.verified,
    npid: row.npid
      ? typeof row.npid === "object"
        ? {
            code: row.npid.code || row.npid.ncid || row.npid.npid || null,
            issued: row.npid.issued ?? Boolean(row.npid.code || row.npid.ncid || row.npid.npid),
            trustScore: row.npid.trustScore ?? row.trustScore ?? null,
          }
        : { code: String(row.npid), issued: true }
      : row.ncid || row.npidCode || row.ncidCode
        ? {
            code: String(row.ncid?.code || row.npidCode || row.ncidCode || row.ncid || ""),
            issued: true,
          }
        : null,
    salaryExpectation:
      row.salaryExpectation != null ? Number(row.salaryExpectation) : row.salary_expectation != null ? Number(row.salary_expectation) : null,
    hourlyRateExpectation:
      row.hourlyRateExpectation != null
        ? Number(row.hourlyRateExpectation)
        : row.hourly_rate_expectation != null
          ? Number(row.hourly_rate_expectation)
          : null,
    shiftPricing:
      row.shiftPricing && typeof row.shiftPricing === "object"
        ? row.shiftPricing
        : row.profileExtended?.shiftPricing && typeof row.profileExtended.shiftPricing === "object"
          ? row.profileExtended.shiftPricing
          : null,
    packages:
      row.packages && typeof row.packages === "object"
        ? row.packages
        : row.profileExtended?.attendantCareHub?.packages &&
            typeof row.profileExtended.attendantCareHub.packages === "object"
          ? row.profileExtended.attendantCareHub.packages
          : null,
    isAvailable: row.isAvailable !== false && row.is_active !== false,
    languages,
    bio: row.bio || "",
    designation: mapCaregiverDesignation(row),
    profileImageUrl: row.profileImageUrl || row.profile_image_url || "",
    profileImageStorageKey:
      row.profileImageStorageKey ||
      row.profile_image_storage_key ||
      extractKeyFromUrl(row.profileImageUrl || row.profile_image_url) ||
      "",
    gender: row.gender || null,
    age: row.age != null ? Number(row.age) : null,
    height: row.height || null,
    weight: row.weight || null,
    religion: row.religion || null,
    foodPreference: row.foodPreference || row.food_preference || null,
    food_preference: row.foodPreference || row.food_preference || null,
    sleepArrangement: row.sleepArrangement || row.sleep_arrangement || null,
    sleep_arrangement: row.sleepArrangement || row.sleep_arrangement || null,
    serviceRole: row.serviceRole || row.careType || null,
    careType: row.serviceRole || row.careType || null,
    workTypes: Array.isArray(row.workTypes)
      ? row.workTypes.map((s) => String(s).trim()).filter(Boolean)
      : parseList(row.workType || row.work_type),
    workType: Array.isArray(row.workTypes) && row.workTypes.length
      ? row.workTypes.join(", ")
      : row.workType || row.work_type || null,
    shiftTypes: Array.isArray(row.shiftTypes)
      ? row.shiftTypes.map((s) => String(s).trim()).filter(Boolean)
      : row.shiftType
        ? [row.shiftType]
        : [],
    shiftType: row.shiftType || null,
    physioFrequency: row.physioFrequency || null,
    physioDuration: row.physioDuration || null,
    level: row.level || row.category || null,
    category: row.category || null,
    emergencyPoolAvailable: Boolean(row.emergencyPoolAvailable),
    latitude,
    longitude,
    mapLatitude: latitude,
    mapLongitude: longitude,
  };
}
